import { graphql } from 'react-relay';
import { useQuery } from '@atlassian/jira-relay-apollo-migration-utils/src/services/use-query/index.tsx';
import type {
	historicalCycleTimeRelayCycleTimeReportQuery$variables,
	historicalCycleTimeRelayCycleTimeReportQuery$data,
} from '@atlassian/jira-relay/src/__generated__/historicalCycleTimeRelayCycleTimeReportQuery.graphql';
import type { ProjectId } from '@atlassian/jira-shared-types/src/general.tsx';
import type { ServiceQueryResultWithFetch } from '@atlassian/jira-software-deployments-service-query-types/src/services/types.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import type { TopFilter } from '../../common/types.tsx';
import { calculateDateRangeFor12Weeks, getCurrentDate } from '../../common/utils/date-range.tsx';
import type { BarChartData } from './types.tsx';
import { buildQueryVariables, transformResult } from './utils.tsx';

export const useHistoricalCycleTimeData = (
	projectId: ProjectId | null,
	filters: TopFilter,
): ServiceQueryResultWithFetch<BarChartData> => {
	const cloudId = useCloudId();
	const { dateRange: twelveWeeksDateRange } = calculateDateRangeFor12Weeks(getCurrentDate());
	const variables = buildQueryVariables(cloudId, projectId, twelveWeeksDateRange, filters);
	const skip = projectId === null;
	const queryResult = useQuery<
		historicalCycleTimeRelayCycleTimeReportQuery$variables,
		historicalCycleTimeRelayCycleTimeReportQuery$data
	>(
		// disabling eslint rule since transformation is happaning inside transformResult()
		/* eslint-disable @atlassian/relay/unused-fields */
		graphql`
			query historicalCycleTimeRelayCycleTimeReportQuery(
				$filter: DevOpsMetricsFilterInput!
				$isIncludeCycleTimeMean: Boolean
			) {
				devOpsMetrics {
					cycleTime(
						filter: $filter
						isIncludeCycleTimeMean: $isIncludeCycleTimeMean
						cycleTimePercentiles: [50]
					) {
						hasPermissionForAllContributingIssues
						cycleTimeMetrics {
							__typename
							data {
								dateTime
								cycleTimeSeconds
								issuesShippedCount
							}
							aggregateData
						}
					}
				}
			}
		`,
		/* eslint-enable @atlassian/relay/unused-fields */
		{
			variables: { ...variables },
			skip,
		},
	);

	return skip ? { loading: true, fetch: () => Promise.resolve() } : transformResult(queryResult);
};
